import { Component } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { RecoveryAgentsService } from '../../services/recovery-agents.service';
import { RecoveryAgentsResult } from '../../models/recovery-agents.model';
import { RecoveryAgent } from '../../models/recoveryagent.model';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { openDeleteConfirmationDialog } from '../../shared/utils/confirm-dialog.utils';
import { formatHttpErrorMessage } from '../../shared/utils/error.utils';
import { resolveLastUpdatedBy } from '../../shared/utils/user.utils';
import { BaseTableComponent } from '../../shared/base/base-table.component';
import { RecoveryAgentModalComponent, RecoveryAgentModalData } from './recovery-agent-modal/recovery-agent-modal.component';
import { RecoveryAgentViewModalComponent } from './recovery-agent-view-modal/recovery-agent-view-modal.component';

@Component({
  selector: 'app-recoveryagents',
  standalone: false,
  templateUrl: './recoveryagents.component.html',
  styleUrls: ['./recoveryagents.component.css'],
})
export class RecoveryagentsComponent extends BaseTableComponent<RecoveryAgent> {
  readonly displayedColumns: string[] = [
    'RAMailingName',
    'Street1',
    'Street2',
    'City',
    'State',
    'ZipCode',
    'PaperlessInd',
    'RecoveryAgentTaxID',
    'OperatingCompany',
    'OfficeKey',
    'View',
    'Edit',
    'Delete'
  ];

  results: RecoveryAgent[] = [];
  pageSizes = [10, 20, 50];
  resultVisibility = true;
  listApiErrorMessage = '';
  operatingCompanyOptions: { id: number; label: string; name: string }[] = [];
  officeKeyOptions: { key: string; label: string; claimOfficeId: number | null }[] = [];
  stateOptions: { code: string; label: string }[] = [];
  dropdownsLoaded = false;
  override sortBy = 'RecoveryAgentID';
  override sortDir: 'asc' | 'desc' = 'asc';

  constructor(private recoveryAgentsService: RecoveryAgentsService, private dialog: MatDialog) {
    super();
  }

  protected override loadData(skip: number, count: number): void {
    this.loading = true;
    this.recoveryAgentsService
      .getRecoveryAgents(skip, count, this.sortBy, this.sortDir, this.searchTerm)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response: RecoveryAgentsResult) => {
          this.results = response.recoveryAgents;
          this.totalCount  = response.totalCount;
          this.listApiErrorMessage = '';
          this.refreshTableData();
        },
        error: (error) => {
          console.error('Failed to load recovery agents', error);
          this.listApiErrorMessage = formatHttpErrorMessage(
            'Failed to load recovery agents',
            error
          );
          this.results = [];
          this.totalCount  = 0;
          this.refreshTableData();
        },
      });
  }

  onAddRecoveryAgent(): void {
    this.listApiErrorMessage = '';
    this.ensureDropdownsLoaded().subscribe({
      next: () => {
        const modalData: RecoveryAgentModalData = {
          isEdit: false,
          record: this.createEmptyRecoveryAgent(),
          operatingCompanyOptions: this.operatingCompanyOptions,
          officeKeyOptions: this.officeKeyOptions,
          stateOptions: this.stateOptions,
        };
        const dialogRef = this.dialog.open(RecoveryAgentModalComponent, {
          width: '860px',
          maxWidth: '95vw',
          maxHeight: '90vh',
          autoFocus: false,
          panelClass: 'recovery-agent-dialog',
          data: modalData,
        });
        dialogRef.componentInstance.submitRecord.subscribe(record => {
          this.createRecoveryAgent(record, dialogRef);
        });
      },
      error: (error) => {
        this.listApiErrorMessage = formatHttpErrorMessage('Failed to load dropdown values', error);
      },
    });
  }

  onView(record: RecoveryAgent): void {
    this.dialog.open(RecoveryAgentViewModalComponent, {
      width: '700px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      autoFocus: false,
      restoreFocus: false,
      data: record,
    });
  }

  onEdit(record: RecoveryAgent): void {
    this.listApiErrorMessage = '';
    this.ensureDropdownsLoaded().subscribe({
      next: () => {
        const modalData: RecoveryAgentModalData = {
          isEdit: true,
          record: { ...record },
          operatingCompanyOptions: this.operatingCompanyOptions,
          officeKeyOptions: this.officeKeyOptions,
          stateOptions: this.stateOptions,
        };
        const dialogRef = this.dialog.open(RecoveryAgentModalComponent, {
          width: '860px',
          maxWidth: '95vw',
          maxHeight: '90vh',
          autoFocus: false,
          panelClass: 'recovery-agent-dialog',
          data: modalData,
        });
        dialogRef.componentInstance.submitRecord.subscribe(result => {
          this.updateRecoveryAgent(result as RecoveryAgent, dialogRef);
        });
      },
      error: (error) => {
        this.listApiErrorMessage = formatHttpErrorMessage('Failed to load dropdown values', error);
      },
    });
  }

  private refreshTableData(): void {
    this.dataSource.data = this.results;
  }

  private updateRecoveryAgent(
    record: RecoveryAgent,
    dialogRef?: MatDialogRef<RecoveryAgentModalComponent>
  ): void {
    this.loading = true;
    const updatedRecord: RecoveryAgent = { ...record, LastUpdatedBy: resolveLastUpdatedBy() };

    this.recoveryAgentsService
      .updateRecoveryAgent(updatedRecord)
      .subscribe({
        next: () => {
          if (dialogRef) {
            dialogRef.close(true);
          }
          this.loadDataWrapper();
        },
        error: (error) => {
          this.loading = false;
          console.error('Failed to update recovery agent', error);
          const message = formatHttpErrorMessage(
            'Failed to update recovery agent',
            error
          );
          if (dialogRef?.componentInstance) {
            dialogRef.componentInstance.dialogError = message;
            return;
          }
          this.listApiErrorMessage = message;
        },
      });
  }

  private createRecoveryAgent(
    record: Partial<RecoveryAgent>,
    dialogRef?: MatDialogRef<RecoveryAgentModalComponent>
  ): void {
    this.loading = true;
    const payload = this.buildCreatePayload(record);

    this.recoveryAgentsService
      .createRecoveryAgent(payload)
      .subscribe({
        next: () => {
          if (dialogRef) {
            dialogRef.close(true);
          }
          this.loadDataWrapper();
        },
        error: (error) => {
          this.loading = false;
          console.error('Failed to create recovery agent', error);
          const message = formatHttpErrorMessage(
            'Failed to create recovery agent',
            error
          );
          if (dialogRef?.componentInstance) {
            dialogRef.componentInstance.dialogError = message;
            return;
          }
          this.listApiErrorMessage = message;
        },
      });
  }

  private ensureDropdownsLoaded(): Observable<void> {
    if (this.dropdownsLoaded) {
      return of(undefined);
    }

    return forkJoin({
      operatingCompanies: this.recoveryAgentsService.getOperatingCompanies(),
      offices: this.recoveryAgentsService.getOffices(),
      states: this.recoveryAgentsService.getRecoveryAgentStates(),
    }).pipe(
      map(({ operatingCompanies, offices, states }) => {
        this.operatingCompanyOptions = this.mapOperatingCompanies(operatingCompanies);
        this.officeKeyOptions = this.mapOffices(offices);
        this.stateOptions = this.mapStates(states);
        this.dropdownsLoaded = true;
      })
    );
  }

  onDelete(record: RecoveryAgent): void {
    this.deleteRecoveryAgent(record);
  }

  private deleteRecoveryAgent(record: RecoveryAgent): void {
    if (!record?.RecoveryAgentID) return;

    const dialogRef = openDeleteConfirmationDialog(
      this.dialog,
      'Delete Recovery Agent',
      `Are you sure you want to remove recovery agent "${record.RAMailingName}"?`
    );

    const recoveryAgentId = record.RecoveryAgentID;

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;

      this.loading = true;
      const lastUpdatedBy = resolveLastUpdatedBy({ includeFullNameFallback: true });
      this.recoveryAgentsService
        .deleteRecoveryAgent(recoveryAgentId, lastUpdatedBy)
        .subscribe({
          next: () => {
            this.loadDataWrapper();
          },
          error: (error) => {
            this.loading = false;
            console.error('Failed to delete recovery agent', error);
            this.listApiErrorMessage = formatHttpErrorMessage(
              'Failed to delete recovery agent',
              error
            );
          },
        });
    })
  }

  private buildCreatePayload(record: Partial<RecoveryAgent>): any {
    const officeKey = (record.OfficeKey ?? '').trim();
    const lastUpdatedBy = resolveLastUpdatedBy({ includeFullNameFallback: true });
    const paperlessInd = (record.PaperlessInd ?? '').trim();

    return {
      RAMailingName: (record.RAMailingName ?? '').trim(),
      Street1: (record.Street1 ?? '').trim(),
      Street2: (record.Street2 ?? '').trim(),
      City: (record.City ?? '').trim(),
      State: (record.State ?? '').trim(),
      ZipCode: (record.ZipCode ?? '').trim(),
      ZipPlus4: (record.ZipPlus4 ?? '').trim(),
      PaperlessInd: paperlessInd || null,
      RecoveryAgentTaxID: (record.RecoveryAgentTaxID ?? '').trim(),
      ExternalOrg: (record.ExternalOrg ?? '').trim(),
      OperatingCompany: (record.OperatingCompany ?? '').trim(),
      OfficeKey: officeKey,
      LastUpdatedBy: lastUpdatedBy,
      OpCompanyID: this.toNullablePositiveNumber(record.OpCompanyID),
      ClaimOfficeID: this.toNullableNumber(record.ClaimOfficeID),
    };
  }

  private createEmptyRecoveryAgent(): Partial<RecoveryAgent> {
    return {
      RAMailingName: '',
      Street1: '',
      Street2: '',
      City: '',
      State: '',
      ZipCode: '',
      ZipPlus4: '',
      PaperlessInd: null,
      RecoveryAgentTaxID: '',
      ExternalOrg: 'YES',
      OperatingCompany: '',
      OfficeKey: '',
      OpCompanyID: null,
      ClaimOfficeID: null,
    };
  }

  private toNullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const converted = Number(value);
    return Number.isFinite(converted) ? converted : null;
  }

  private toNullablePositiveNumber(value: unknown): number | null {
    const converted = this.toNullableNumber(value);
    return converted !== null && converted > 0 ? converted : null;
  }

  private mapOperatingCompanies(response: any): { id: number; label: string; name: string }[] {
    return this.extractResponseArray(response)
      .map((item) => {
        if (item === null || item === undefined) {
          return null;
        }

        if (typeof item === 'string' || typeof item === 'number') {
          const primitiveLabel = String(item).trim();
          return primitiveLabel
            ? { id: 0, label: primitiveLabel, name: primitiveLabel }
            : null;
        }

        const id = this.toNullablePositiveNumber(
          item?.OpCompanyID ??
          item?.opCompanyID ??
          item?.OpCompanyId ??
          item?.opCompanyId ??
          item?.OperatingCompanyID ??
          item?.operatingCompanyID ??
          item?.operatingCompanyId ??
          item?.id ??
          item?.value
        );

        const opCompanyKey = String(
          item?.OpCompanyKey ??
          item?.opCompanyKey ??
          item?.OperatingCompanyKey ??
          item?.operatingCompanyKey ??
          ''
        ).trim();

        const code = String(
          item?.OpCompanyCode ?? item?.opCompanyCode ?? item?.Code ?? item?.code ?? ''
        ).trim();
        const name = String(
          item?.OperatingCompany ??
          item?.operatingCompany ??
          item?.OperatingCompanyName ??
          item?.operatingCompanyName ??
          item?.MailingName ??
          item?.mailingName ??
          item?.Name ??
          item?.name ??
          item?.Description ??
          item?.description ??
          ''
        ).trim();

        const keyOrCode = opCompanyKey || code;
        const label = keyOrCode && name ? `${keyOrCode}: ${name}` : name || keyOrCode;

        return id !== null && String(label).trim().length > 0
          ? { id, label: String(label).trim(), name }
          : null;
      })
      .filter((item): item is { id: number; label: string; name: string } => item !== null);
  }

  private mapOffices(response: any): { key: string; label: string; claimOfficeId: number | null }[] {
    return this.extractResponseArray(response)
      .map((item) => {
        if (item === null || item === undefined) {
          return null;
        }

        if (typeof item === 'string' || typeof item === 'number') {
          const primitiveKey = String(item).trim();
          return primitiveKey
            ? { key: primitiveKey, label: primitiveKey, claimOfficeId: null }
            : null;
        }

        const claimOfficeId = this.toNullableNumber(
          item?.ClaimOfficeID ?? item?.claimOfficeID ?? item?.OfficeId ?? item?.officeId ?? null
        );

        const officeName = String(
          item?.OfficeName ??
          item?.officeName ??
          item?.MailingName ??
          item?.mailingName ??
          item?.Name ??
          item?.name ??
          ''
        ).trim();

        const officeCode = String(
          item?.OfficeKey ??
          item?.officeKey ??
          item?.OfficeCode ??
          item?.officeCode ??
          item?.Key ??
          item?.key ??
          item?.Code ??
          item?.code ??
          ''
        ).trim();

        const key = officeName || officeCode;
        const label = officeName
          ? `${officeName} - ${officeCode}`
          : officeName

        return key ? { key, label, claimOfficeId } : null;
      })
      .filter(
        (item): item is { key: string; label: string; claimOfficeId: number | null } => item !== null
      );
  }

  private mapStates(response: any): { code: string; label: string }[] {
    return this.extractResponseArray(response)
      .map((item) => {
        if (item === null || item === undefined) {
          return null;
        }

        if (typeof item === 'string') {
          const normalized = item.trim();
          return normalized ? { code: normalized, label: normalized } : null;
        }

        const code = String(
          item?.StateCode ?? item?.stateCode ?? item?.State ?? item?.state ?? item?.Code ?? item?.code ?? ''
        ).trim();
        const label = String(item?.StateName ?? item?.stateName ?? code).trim();

        return code ? { code, label } : null;
      })
      .filter((item): item is { code: string; label: string } => item !== null);
  }

  private extractResponseArray(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (Array.isArray(response?.items)) {
      return response.items;
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    if (Array.isArray(response?.value)) {
      return response.value;
    }

    if (response && typeof response === 'object') {
      const firstArray = Object.values(response).find((value) => Array.isArray(value));
      if (Array.isArray(firstArray)) {
        return firstArray;
      }
    }

    return [];
  }

}
